// src/pages/Analytics.js
import React, { useState, useEffect } from 'react';
import './Analytics.css';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'http://localhost:8000';

  useEffect(() => {
    fetchAnalyticsData();
    // Track this page visit
    trackPageVisit('analytics');
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/analytics/data`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalyticsData(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const trackPageVisit = async (page) => {
    try {
      await fetch(`${API_BASE_URL}/analytics/track-visit?page=${page}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      console.error('Error tracking visit:', err);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getLocationFromIP = (ip) => {
    // This is a placeholder - in a real app you'd use a geolocation service
    // For now, we'll just show the IP address
    return ip === '127.0.0.1' ? 'Local Development' : `IP: ${ip}`;
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-container">
          <header className="analytics-header">
            <h1 className="analytics-title">Analytics Dashboard</h1>
            <div className="analytics-accent" aria-hidden="true" />
            <p className="analytics-subtitle">
              Real-time visitor analytics and insights
            </p>
          </header>

          <div className="analytics-placeholder">
            <div className="placeholder-icon">📊</div>
            <h2 className="placeholder-title">Analytics Dashboard Coming Soon</h2>
            
            <div className="placeholder-content">
              <p className="placeholder-description">
                The backend infrastructure for this analytics dashboard is currently under development. 
                Once complete, this page will provide:
              </p>

              <div className="analytics-features">
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div className="feature-details">
                    <strong>Real-Time Visitor Tracking</strong>
                    <p>Monitor site visitors in real-time with IP tracking and geolocation</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div className="feature-details">
                    <strong>Traffic Analytics</strong>
                    <p>Detailed insights on page views, unique visitors, and user behavior</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div className="feature-details">
                    <strong>Custom Metrics</strong>
                    <p>Track custom events and KPIs specific to your needs</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div className="feature-details">
                    <strong>Data Visualization</strong>
                    <p>Interactive charts and graphs for easy data interpretation</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <div className="feature-details">
                    <strong>Export & Reporting</strong>
                    <p>Generate detailed reports and export data for further analysis</p>
                  </div>
                </div>
              </div>

              <div className="backend-status">
                <div className="status-badge">
                  <span className="status-indicator pending"></span>
                  <span className="status-text">Backend API: In Development</span>
                </div>
                <p className="status-note">
                  The Python/FastAPI backend with Firebase integration is currently being built.
                </p>
              </div>

              <div className="tech-preview">
                <p className="tech-preview-label">Technology Stack:</p>
                <div className="tech-items">
                  <span className="tech-item">Python FastAPI</span>
                  <span className="tech-item">Firebase Firestore</span>
                  <span className="tech-item">Real-time Analytics</span>
                  <span className="tech-item">React Dashboard</span>
                </div>
              </div>

              <button onClick={fetchAnalyticsData} className="retry-btn">
                🔄 Check Connection
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        <header className="analytics-header">
          <h1 className="analytics-title">Analytics Dashboard</h1>
          <div className="analytics-accent" aria-hidden="true" />
          <p className="analytics-subtitle">
            Real-time visitor analytics and IP tracking
          </p>
        </header>

        {analyticsData && (
          <>
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon">📊</div>
                <div className="card-content">
                  <h3>Total Visits</h3>
                  <p className="card-value">{analyticsData.total_visits}</p>
                </div>
              </div>

              <div className="summary-card">
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <h3>Unique Visitors</h3>
                  <p className="card-value">{analyticsData.unique_visitors}</p>
                </div>
              </div>

              <div className="summary-card">
                <div className="card-icon">🕒</div>
                <div className="card-content">
                  <h3>Last Updated</h3>
                  <p className="card-value">
                    {formatTimestamp(analyticsData.last_updated)}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Visits Table */}
            <div className="visits-section">
              <h2>Recent Visits</h2>
              <div className="table-container">
                <table className="visits-table">
                  <thead>
                    <tr>
                      <th>IP Address</th>
                      <th>Location</th>
                      <th>Page</th>
                      <th>Timestamp</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.recent_visits.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="no-data">
                          No visits recorded yet
                        </td>
                      </tr>
                    ) : (
                      analyticsData.recent_visits.map((visit, index) => (
                        <tr key={index}>
                          <td className="ip-address">{visit.ip_address}</td>
                          <td className="location">
                            {getLocationFromIP(visit.ip_address)}
                          </td>
                          <td className="page">
                            <span className="page-badge">{visit.page}</span>
                          </td>
                          <td className="timestamp">
                            {formatTimestamp(visit.timestamp)}
                          </td>
                          <td className="user-agent" title={visit.user_agent}>
                            {visit.user_agent ? 
                              visit.user_agent.substring(0, 50) + '...' : 
                              'Unknown'
                            }
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="actions">
              <button onClick={fetchAnalyticsData} className="refresh-btn">
                🔄 Refresh Data
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;